/**
 * LLM Trial Manager Utility
 * Manages AI Mentor access - checks 7-day LLM trial FIRST, then falls back to premium subscription
 * LLM trial (7 days) takes precedence over premium subscription for trial period
 */

import { checkPaymentStatus } from './paymentStatus';
import API_BASE_URL from '../Config/config';

export const initializeLLMTrial = (userId) => {
  const trialKey = `llm_trial_${userId}`;
  const existingTrial = localStorage.getItem(trialKey);
  
  if (!existingTrial) {
    const trialStart = new Date();
    const trialEnd = new Date(trialStart.getTime() + 7 * 24 * 60 * 60 * 1000); // 7 days
    const trial = {
      startDate: trialStart.toISOString(),
      endDate: trialEnd.toISOString(),
      used: false
    };
    localStorage.setItem(trialKey, JSON.stringify(trial));
    return trial;
  }
  
  return JSON.parse(existingTrial);
};

export const checkLLMTrialStatus = async (userId) => {
  if (!userId) {
    return {
      hasActiveTrial: false,
      daysRemaining: 0,
      isExpired: true,
      source: 'none'
    };
  }

  // ✅ FIRST: Check for active 7-day LLM trial (from database)
  // LLM trial should take precedence and show 7 days, not premium subscription days
  try {
    const trialResponse = await fetch(`${API_BASE_URL}/api/skillgenie-premium/check-llm-trial/${userId}`, {
      signal: AbortSignal.timeout(5000)
    });

    if (trialResponse.ok) {
      const trialData = await trialResponse.json();
      if (trialData.ok && trialData.hasActiveTrial && trialData.trial) {
        // User has active 7-day LLM trial - return 7 days
        return {
          hasActiveTrial: true,
          daysRemaining: trialData.trial.daysRemaining || 0,
          isExpired: (trialData.trial.daysRemaining || 0) <= 0,
          source: 'llm_trial',
          trial: {
            planName: trialData.trial.plan_name || 'llm_trial_7days',
            startDate: trialData.trial.start_date,
            endDate: trialData.trial.end_date
          }
        };
      }
    }
  } catch (err) {
    console.warn('[LLM Trial] Failed to check LLM trial from API, trying fallbacks:', err);
  }

  // ✅ SECOND: Check for premium subscription (for users without LLM trial)
  try {
    const paymentStatus = await checkPaymentStatus(userId);
    if (paymentStatus.hasPayment && paymentStatus.isValid) {
      // User has premium subscription but no LLM trial - use premium days
      return {
        hasActiveTrial: true,
        daysRemaining: paymentStatus.daysLeft || 0,
        isExpired: false,
        source: 'premium',
        subscription: paymentStatus
      };
    }
  } catch (err) {
    console.warn('[LLM Trial] Failed to check premium status:', err);
  }

  // ✅ THIRD: Fall back to localStorage 7-day trial if nothing else found
  const trial = initializeLLMTrial(userId);
  const now = new Date();
  const endDate = new Date(trial.endDate);
  const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  
  return {
    hasActiveTrial: endDate > now,
    daysRemaining: Math.max(0, daysRemaining),
    trial,
    isExpired: daysRemaining <= 0,
    source: 'local_storage_trial'
  };
};

export const handleMentorAccess = async (userId) => {
  const status = await checkLLMTrialStatus(userId);
  return status;
};





